import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { Workflow } from '../workflow'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class BeginEndBlock extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'oracle.beginendblock',
        group: 'Oracle',
        title: 'Begin/End Block',
        description: `
        This component executes an arbitrary PL/SQL block for Oracle databases.

        Use $a and $b as placeholders for the input table names
        and $output as placeholder for the output table name.

        `,
        starred: true
      },
      [
        {
          name: 'sourcea',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'sourceb',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'sql',
          type: WorkflowComponentParamType.StringSql,
          title: 'PL/SQL Block',
          mode: WorkflowComponentParameterMode.Multiline,
          placeholder:
            "CREATE OR REPLACE DIRECTORY my_dir AS '/data/files';\nEXECUTE IMMEDIATE 'CREATE TABLE $output AS SELECT * FROM $a';",
          allowExpressions: false
        }
      ]
    )
  }

  supportedProviders(): Provider[] {
    return [Provider.Oracle]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const isStoredProcedure = codeType === WorkflowCodeType.StoredProcedure
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)

    let query = (params.sql as string) ?? ''

    // For Oracle, use quoteName method to clean up table names
    query = query
      .replace(/\$a/g, connection.quoteName(params.sourcea as string))
      .replace(/\$b/g, connection.quoteName(params.sourceb as string))
      .replace(/\$output/g, tablename)
      .replace(/\$dryrun/g, dryRun ? '1' : '0')
      .replace(/\$isStoredProcedure/g, isStoredProcedure ? '1' : '0')

    query += query.endsWith(';') ? '' : ';'
    query = dedent`${connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)}`

    query = dedent`
      BEGIN
      ${query}
      END;`

    return {
      query,
      outputs: { result: tablename }
    }
  }

  isOutputTableCreated(
    workflow: Workflow,
    componentName: string,
    outputName: string,
    params: Record<string, unknown>
  ): boolean {
    // Return true if $output is used in the SQL, or always return true to allow dependencies
    const sql = params.sql as string
    return sql ? /\$output/g.test(sql) : true // Allow dependencies even without $output
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '1'
  }
}

export { BeginEndBlock }
